import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import {
  Map as MapGL,
  Marker,
  NavigationControl,
  type MapRef,
} from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { Button } from "@/components/ui/button";
import { AlertMarker } from "@/components/AlertMarker";
import { AlertList } from "@/components/AlertList";
import { ReportSheet } from "@/components/ReportSheet";
import { useWazeAlerts } from "@/hooks/useWazeAlerts";
import { bboxFromCenter } from "@/lib/bbox";
import { stadiaStyleUrl } from "@/lib/tile-style";
import { cn } from "@/lib/utils";
import type { WazeAlert } from "@shared/types";
import { Locate, Moon, Sun } from "lucide-react";

const FLOATING_CONTROL_BOX_SHADOW = "0 0 0 2px rgba(0,0,0,.1)";
const floatingControlStyle = { boxShadow: FLOATING_CONTROL_BOX_SHADOW };
const WAZE_GESTURE_MIN_TOUCHES = 3;
const WAZE_GESTURE_MAX_TAP_MS = 350;
const WAZE_GESTURE_DOUBLE_TAP_MS = 550;
const WAZE_GESTURE_MOVE_TOLERANCE_PX = 30;

interface MapViewProps {
  geo: { lat: number; lon: number; heading: number | null; speedMps: number | null };
  stadiaApiKey: string;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  onOpenWaze?: () => void;
}

export function MapView({
  geo,
  stadiaApiKey,
  theme,
  onToggleTheme,
  onOpenWaze,
}: MapViewProps) {
  const mapRef = useRef<MapRef | null>(null);
  const keepActiveAlertOnNextMove = useRef(false);
  const wazeGestureRef = useRef(createWazeGestureState());
  const [follow, setFollow] = useState(true);
  const [bbox, setBbox] = useState(() => bboxFromCenter(geo.lat, geo.lon));
  const [activeId, setActiveId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(14);

  const styleUrl = useMemo(
    () =>
      stadiaStyleUrl(
        stadiaApiKey,
        theme === "dark" ? "alidade_smooth_dark" : "alidade_smooth",
      ),
    [stadiaApiKey, theme],
  );
  const alertMarkerScale = getAlertMarkerScale(zoom);
  const isPanning = !follow;

  useEffect(() => {
    if (!follow || !mapRef.current) return;
    mapRef.current.easeTo({
      center: [geo.lon, geo.lat],
      bearing: geo.heading ?? 0,
      duration: 600,
    });
  }, [geo.lat, geo.lon, geo.heading, follow]);

  const { data, loading } = useWazeAlerts(bbox);

  const alerts: WazeAlert[] = data?.alerts ?? [];
  const activeAlert = alerts.find((a) => a.uuid === activeId) ?? null;

  const handleTouchStartCapture = (event: TouchEvent<HTMLDivElement>) => {
    if (!onOpenWaze) return;

    const state = wazeGestureRef.current;
    if (event.touches.length === 1) {
      state.startTime = window.performance.now();
      state.startPoints.clear();
      state.maxTouches = 0;
      state.moved = false;
    }

    state.maxTouches = Math.max(state.maxTouches, event.touches.length);
    for (const touch of Array.from(event.changedTouches)) {
      state.startPoints.set(touch.identifier, {
        x: touch.clientX,
        y: touch.clientY,
      });
    }
  };

  const handleTouchMoveCapture = (event: TouchEvent<HTMLDivElement>) => {
    if (!onOpenWaze) return;

    const state = wazeGestureRef.current;
    for (const touch of Array.from(event.touches)) {
      const start = state.startPoints.get(touch.identifier);
      if (!start) continue;

      const distance = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
      if (distance > WAZE_GESTURE_MOVE_TOLERANCE_PX) {
        state.moved = true;
        break;
      }
    }
  };

  const handleTouchEndCapture = (event: TouchEvent<HTMLDivElement>) => {
    if (!onOpenWaze || event.touches.length > 0) return;

    const state = wazeGestureRef.current;
    const now = window.performance.now();
    const isWazeTap =
      state.maxTouches >= WAZE_GESTURE_MIN_TOUCHES &&
      !state.moved &&
      now - state.startTime <= WAZE_GESTURE_MAX_TAP_MS;

    if (isWazeTap) {
      const isDoubleTap = now - state.lastTapTime <= WAZE_GESTURE_DOUBLE_TAP_MS;
      state.lastTapTime = now;

      if (isDoubleTap) {
        event.preventDefault();
        event.stopPropagation();
        onOpenWaze();
      }
    }

    state.startPoints.clear();
    state.maxTouches = 0;
    state.moved = false;
    state.startTime = 0;
  };

  const handleTouchCancelCapture = () => {
    resetWazeGestureState(wazeGestureRef.current);
  };

  return (
    <div
      className="absolute inset-0"
      onTouchStartCapture={handleTouchStartCapture}
      onTouchMoveCapture={handleTouchMoveCapture}
      onTouchEndCapture={handleTouchEndCapture}
      onTouchCancelCapture={handleTouchCancelCapture}
    >
      <MapGL
        ref={(m) => {
          mapRef.current = m;
        }}
        mapStyle={styleUrl}
        onClick={() => setActiveId(null)}
        onMoveStart={() => {
          if (keepActiveAlertOnNextMove.current) {
            keepActiveAlertOnNextMove.current = false;
            return;
          }

          setActiveId(null);
        }}
        initialViewState={{
          longitude: geo.lon,
          latitude: geo.lat,
          zoom: 14,
          bearing: geo.heading ?? 0,
          pitch: 0,
        }}
        onMoveEnd={(evt) => {
          const b = evt.target.getBounds();
          setZoom(evt.target.getZoom());
          setBbox({
            top: b.getNorth(),
            bottom: b.getSouth(),
            left: b.getWest(),
            right: b.getEast(),
          });
        }}
        onDragStart={() => setFollow(false)}
        attributionControl={false}
      >
        <NavigationControl
          position="bottom-left"
          showCompass
          visualizePitch
          style={{
            margin: "1rem",
            overflow: "hidden",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-xl)",
            boxShadow: FLOATING_CONTROL_BOX_SHADOW,
            background: "color-mix(in oklch, var(--card) 78%, transparent)",
          }}
        />
        <style>
          {`
            .maplibregl-ctrl-bottom-left .maplibregl-ctrl-group button + button {
              border-top: 1px solid var(--border);
            }

            .maplibregl-ctrl-bottom-left .maplibregl-ctrl-group button .maplibregl-ctrl-icon {
              background-color: var(--foreground);
              background-image: none;
              background-position: center;
              background-repeat: no-repeat;
              mask-position: center;
              mask-repeat: no-repeat;
              mask-size: 20px 20px;
              -webkit-mask-position: center;
              -webkit-mask-repeat: no-repeat;
              -webkit-mask-size: 20px 20px;
            }

            .maplibregl-ctrl-bottom-left .maplibregl-ctrl-zoom-in .maplibregl-ctrl-icon {
              mask-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 3h2v6h6v2h-6v6H9v-6H3V9h6V3z' fill='black'/%3E%3C/svg%3E");
              -webkit-mask-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 3h2v6h6v2h-6v6H9v-6H3V9h6V3z' fill='black'/%3E%3C/svg%3E");
            }

            .maplibregl-ctrl-bottom-left .maplibregl-ctrl-zoom-out .maplibregl-ctrl-icon {
              mask-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 9h14v2H3V9z' fill='black'/%3E%3C/svg%3E");
              -webkit-mask-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 9h14v2H3V9z' fill='black'/%3E%3C/svg%3E");
            }

            .maplibregl-ctrl-bottom-left .maplibregl-ctrl-compass .maplibregl-ctrl-icon {
              mask-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 2 4.5 18 10 14.5 15.5 18 10 2z' fill='black'/%3E%3C/svg%3E");
              -webkit-mask-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 2 4.5 18 10 14.5 15.5 18 10 2z' fill='black'/%3E%3C/svg%3E");
            }
          `}
        </style>
        <Marker longitude={geo.lon} latitude={geo.lat} anchor="center">
          <div className="relative size-6">
            <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-primary/60" />
            <span
              className="relative block size-6 rounded-full bg-primary shadow-lg ring-4 ring-primary/30"
              style={{
                transform: geo.heading
                  ? `rotate(${geo.heading}deg)`
                  : undefined,
              }}
            />
          </div>
        </Marker>

        {alerts.map((a) => {
          if (!a.location) return null;
          return (
            <Marker
              key={a.uuid}
              longitude={a.location.x}
              latitude={a.location.y}
              anchor="bottom"
            >
              <AlertMarker
                alert={a}
                active={a.uuid === activeId}
                scale={alertMarkerScale}
                onClick={(event) => {
                  event.stopPropagation();
                  setActiveId(a.uuid);
                  setFollow(false);
                  keepActiveAlertOnNextMove.current = true;
                  mapRef.current?.easeTo({
                    center: [a.location.x, a.location.y],
                    duration: 500,
                  });
                }}
              />
            </Marker>
          );
        })}

        {activeAlert?.location && (
          <Marker
            longitude={activeAlert.location.x}
            latitude={activeAlert.location.y}
            anchor="bottom"
            offset={[0, -getActiveAlertCardOffset(alertMarkerScale)]}
            style={{ zIndex: 9999 }}
          >
            <ActiveAlertCard
              alert={activeAlert}
              onClose={() => setActiveId(null)}
            />
          </Marker>
        )}
      </MapGL>

      <div className="pointer-events-none absolute inset-0">
        <div className="pointer-events-auto safe-top absolute left-0 top-0 p-4">
          <Button
            title={
              isPanning
                ? "Tap to recenter"
                : "Centered on location"
            }
            variant="glass"
            size={isPanning ? "default" : "icon"}
            style={floatingControlStyle}
            className={cn(
              "transition-all",
              isPanning &&
                "border-primary/70 bg-primary/20 px-4 text-primary shadow-primary/20",
            )}
            onClick={() => {
              setFollow(true);
              mapRef.current?.flyTo({
                center: [geo.lon, geo.lat],
                zoom: 14,
                duration: 600,
              });
            }}
          >
            <Locate className={cn("size-6", isPanning && "animate-pulse")} />
          </Button>
        </div>
        <div className="pointer-events-auto safe-top absolute right-0 top-0 p-4">
          <div className="flex flex-col items-end gap-2">
            <div className="inline-flex rounded-2xl" style={floatingControlStyle}>
              <ReportSheet />
            </div>
            <div className="inline-flex rounded-2xl" style={floatingControlStyle}>
              <AlertList
                alerts={alerts}
                origin={{ lat: geo.lat, lon: geo.lon }}
                loading={loading}
                onSelect={(a) => {
                  setActiveId(a.uuid);
                  setFollow(false);
                  if (a.location) {
                    keepActiveAlertOnNextMove.current = true;
                    mapRef.current?.flyTo({
                      center: [a.location.x, a.location.y],
                      zoom: 15,
                      duration: 700,
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="pointer-events-auto safe-bottom absolute bottom-0 right-0 p-4">
          <div className="flex flex-col items-end gap-2">
            <Button
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              variant="glass"
              size="icon"
              style={floatingControlStyle}
              onClick={onToggleTheme}
            >
              {theme === "dark" ? (
                <Sun className="size-6" />
              ) : (
                <Moon className="size-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
}

function createWazeGestureState() {
  return {
    startTime: 0,
    startPoints: new Map<number, { x: number; y: number }>(),
    maxTouches: 0,
    moved: false,
    lastTapTime: Number.NEGATIVE_INFINITY,
  };
}

function resetWazeGestureState(state: ReturnType<typeof createWazeGestureState>) {
  state.startPoints.clear();
  state.maxTouches = 0;
  state.moved = false;
  state.startTime = 0;
}

function getAlertMarkerScale(zoom: number) {
  const compactZoom = 9;
  const fullSizeZoom = 13;
  const compactScale = 0.5;

  if (zoom <= compactZoom) return compactScale;
  if (zoom >= fullSizeZoom) return 1;

  const progress = (zoom - compactZoom) / (fullSizeZoom - compactZoom);
  return compactScale + progress * (1 - compactScale);
}

function getActiveAlertCardOffset(markerScale: number) {
  const markerSize = 48;
  const activeMarkerScale = markerScale * 1.25;
  const gap = 10;

  return markerSize * activeMarkerScale + gap;
}

function ActiveAlertCard({
  alert,
  onClose,
}: {
  alert: WazeAlert;
  onClose: () => void;
}) {
  return (
    <div
      className="pointer-events-auto glass z-9999 whitespace-nowrap rounded-2xl border border-border px-5 py-3 shadow-xl"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-center gap-3">
        <span className="font-semibold">{alert.type}</span>
        <span className="text-sm text-muted-foreground">
          {alert.street ?? alert.city ?? ""}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-2 text-sm text-muted-foreground"
        >
          Close
        </button>
      </div>
    </div>
  );
}
